import React, { useState } from 'react'
import {
  Box,
  Button,
  Heading,
  Typography,
  Frame,
  Select,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  MenuItem,
  Chip
} from 'components'
import { ProgressBarFooter } from './FreelancerProgressBarFooter'
import firebase from 'utils/firebase'
import { updateUser, useContent } from 'utils'

// Custom square radio component
const SquareRadio = (props) => {
  return (
    <Radio
      {...props}
      disableRipple
      color="primary"
      style={{
        padding: 9,
        ...props.style
      }}
      icon={<div style={{ 
        width: 20, 
        height: 20, 
        border: '2px solid rgba(0, 0, 0, 0.54)',
        borderRadius: 0, 
        boxSizing: 'border-box',
        backgroundColor: '#fff', 
        display: 'inline-block',
        transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms'
      }} />}
      checkedIcon={<div style={{ 
        width: 20, 
        height: 20, 
        border: '2px solid #3f51b5',
        borderRadius: 0, 
        backgroundColor: '#3f51b5', 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms'
      }}>
        <div style={{
          width: 10,
          height: 10,
          backgroundColor: '#fff'
        }} />
      </div>}
    />
  )
}

const FreelancerUnionStatus = ({
  onboarding,
  user,
  stepIndex,
  totalSteps,
  onNext = () => {},
  onSubmit = () => {}
}) => {
  const [unionStatus, setUnionStatus] = useState(user?.unionStatus || '')
  const [selectedUnions, setSelectedUnions] = useState(user?.selectedUnions || [])
  const [currentUnion, setCurrentUnion] = useState('')
  const [currentMemberType, setCurrentMemberType] = useState('')
  
  // Get unions from content
  const { unions, landing } = useContent() || {}
  const buttonColor = landing?.['onboarding-button-color'] || 'primary'
  const buttonSecondaryColor = landing?.['onboarding-button-secondary'] || '#6c757d'
  
  // Placeholder unions if the array is empty
  const availableUnions = unions && unions.length > 0 ? unions : [
    'IATSE',
    'SAG-AFTRA'
  ]
  
  // Get unions that are not already selected
  const unselectedUnions = availableUnions.filter(
    union => !selectedUnions.some(selected => selected === union || (selected && selected.name === union))
  )
  
  const handleAddUnion = () => {
    if (!currentUnion || !currentMemberType) return
    
    // Since unions are strings, we can add them directly with the memberType
    setSelectedUnions([...selectedUnions, {
      name: currentUnion, // Store the union name
      memberType: currentMemberType
    }])
    setCurrentUnion('')
    setCurrentMemberType('')
  }
  
  const handleRemoveUnion = (unionName) => {
    setSelectedUnions(selectedUnions.filter(union => union.name !== unionName))
  }
  
  const handleSubmit = () => {
    if (unionStatus) {
      // Save to user profile
      if (onboarding) {
        firebase.analytics().logEvent('user_freelancer_union_status_submit_click')
      }
      
      // Include all relevant data
      const data = { 
        unionStatus, 
        ...(unionStatus !== 'Non-Union' ? { selectedUnions } : {})
      }
      
      // Use updateUser utility to update the user profile
      updateUser(user, data).then(() => {
        if (onSubmit) {
          onSubmit(data)
        }
        
        if (onNext) {
          onNext()
        }
      })
    }
  }
  
  // If the union status is Non-Union, we don't need to collect union information
  const showUnionFields = unionStatus === 'Union' || unionStatus === 'Both Union and Non-Union'
  
  return (
    <Frame maxWidth="xs" user={user}>
      <Box mx="auto" maxWidth={430}>
        <Heading>
          Can you work as Union or Non-Union?
        </Heading>
        
        <Box my={3}>
          <FormControl component="fieldset">
            <RadioGroup
              aria-label="union-status"
              name="union-status"
              value={unionStatus}
              onChange={(e) => setUnionStatus(e.target.value)}
            >
              <FormControlLabel
                value="Non-Union"
                control={<SquareRadio />}
                label="Non-Union"
              />
              <FormControlLabel
                value="Union"
                control={<SquareRadio />}
                label="Union"
              />
              <FormControlLabel
                value="Both Union and Non-Union"
                control={<SquareRadio />}
                label="Both Union and Non-Union"
              />
            </RadioGroup>
          </FormControl>
        </Box>
        
        {showUnionFields && (
          <>
            <Typography variant="h6" style={{ marginTop: 24, marginBottom: 16 }}>
              Select any Union(s) you're a member of
            </Typography>
            
            {/* Display selected unions as chips */}
            <Box display="flex" flexWrap="wrap" mb={2}>
              {selectedUnions.map((union, index) => (
                <Chip
                  key={`${union.name}-${index}`}
                  label={`${union.name} (${union.memberType})`}
                  onDelete={() => handleRemoveUnion(union.name)}
                  color="primary"
                  style={{ margin: '4px' }}
                />
              ))}
            </Box>
            
            {/* Union and member type selection */}
            <Box mb={3}>
              <Box display="flex" flexDirection="column" mb={2}>
                <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
                  Select Union
                </Typography>
                <Select
                  placeholder="Select a union"
                  value={currentUnion}
                  onChange={(e) => setCurrentUnion(e.target.value)}
                  style={{ width: '100%' }}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    <em>Select a union</em>
                  </MenuItem>
                  {unselectedUnions.map((union) => (
                    <MenuItem key={union} value={union}>
                      {union}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              
              <Box display="flex" flexDirection="column" mb={2}>
                <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
                  Member Type
                </Typography>
                <FormControl component="fieldset">
                  <RadioGroup
                    aria-label="member-type"
                    name="member-type"
                    value={currentMemberType}
                    onChange={(e) => setCurrentMemberType(e.target.value)}
                    row
                  >
                    <FormControlLabel
                      value="Full"
                      control={<SquareRadio />}
                      label="Full"
                    />
                    <FormControlLabel
                      value="Associate"
                      control={<SquareRadio />}
                      label="Associate"
                    />
                    <FormControlLabel
                      value="Permitee"
                      control={<SquareRadio />}
                      label="Permitee"
                    />
                    <FormControlLabel
                      value="Apprentice"
                      control={<SquareRadio />}
                      label="Apprentice"
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
              
              <Button 
                variant="contained" 
                style={{ 'background-color': buttonColor, color: 'white' }}
                onClick={handleAddUnion}
                disabled={!currentUnion || !currentMemberType}
                fullWidth
              >
                Add Union
              </Button>
            </Box>
          </>
        )}
      </Box>
      
      {onboarding && (
        <ProgressBarFooter
          valid={!!unionStatus}
          stepIndex={stepIndex}
          firstStep={stepIndex}
          numSteps={totalSteps}
          onNext={handleSubmit}
          onPrev={() => {}}
        />
      )}
      
      {!onboarding && (
        <Box mx="auto" my={3} maxWidth={280}>
          <Button
            variant="contained"
            type="submit"
            style={{ width: '100%', 'background-color': buttonColor }}
            onClick={handleSubmit}
            disabled={!unionStatus}
          >
            Next
          </Button>
        </Box>
      )}
    </Frame>
  )
}

export default FreelancerUnionStatus
